// Ask IronSight (the FastTrack agent) — the logic shared by today's tab
// (UnitDetailPane/FastTrackAgentTab) and the full unit page
// (UnitV2/tabs/AskTabV2), so the two cannot drift: the thinking loader's
// steps, the risk badge, the marketplace percentile / trend buckets, badges and
// insight lines, the cached timestamp, and the task built from a
// recommendation.

import SOURCE_LABELS from '../config/sourceLabels';

// ── Thinking loader (P-UNIT-044) ─────────────────────────────────────────

export const THINKING_STEPS = Object.freeze([
    { icon: '⚖️', label: 'Fetching active market peers', detail: 'Comps, pricing, days on lot' },
    { icon: '🏷️', label: `Reading ${SOURCE_LABELS.listingLower}`, detail: 'Quality score, description, options' },
    { icon: '💬', label: 'Checking quote pipeline', detail: 'Open quotes, lost deals, buyer history' },
    { icon: '📊', label: 'Analysing pricing vs. AI prediction', detail: 'Price gap, market position' },
    { icon: '📣', label: `Pulling ${SOURCE_LABELS.marketplaceLower} engagement`, detail: 'Impressions, CTR, watchlists, leads (30d)' },
    { icon: '🌐', label: `Pulling ${SOURCE_LABELS.websiteLower} activity`, detail: 'Pageviews, sessions, bounce rate' },
    { icon: '🔍', label: 'Comparing listing performance', detail: 'Photos, description, completeness vs peers' },
    { icon: '✦', label: 'Generating recommendations…', detail: 'Prioritised actions for your sales team' },
]);

// How long each step shows before the next; the last one waits for the answer.
export const STEP_DURATIONS = Object.freeze([2000, 1800, 1600, 2200, 1800, 1800, 1800, 99999]);

/** 'done' | 'active' | 'pending' for step i while the loader is at `step`. */
export const stepState = (i, step) => (i < step ? 'done' : i === step ? 'active' : 'pending');

// ── Formatting ───────────────────────────────────────────────────────────

export function fmtNum(v, digits = 0) {
    if (v == null || Number.isNaN(v)) return '—';
    return Number(v).toLocaleString(undefined, { maximumFractionDigits: digits, minimumFractionDigits: digits > 0 ? digits : 0 });
}

export function fmtPct(v, digits = 1) {
    if (v == null || Number.isNaN(v)) return '—';
    const sign = v > 0 ? '+' : '';
    return `${sign}${Number(v).toFixed(digits)}%`;
}

// ── Marketplace performance (P-UNIT-055) ─────────────────────────────────

/** Percentile colour bucket: <20 low, <40 mid, else high (reverse for bounce). */
export function percentileBucket(p, reverse = false) {
    if (p == null) return 'na';
    const v = reverse ? 100 - p : p;
    if (v < 20) return 'low';
    if (v < 40) return 'mid';
    return 'high';
}

/** 7d vs prior: ≥ +5 up, ≤ −20 down, otherwise flat. */
export function trendBucket(v) {
    if (v == null) return 'na';
    if (v >= 5) return 'up';
    if (v <= -20) return 'down';
    return 'flat';
}

export function trendArrow(v) {
    if (v == null) return '–';
    if (v > 0) return '▲';
    if (v < 0) return '▼';
    return '→';
}

/** The marketplace rows, in today's order: [label, block, unit, digits, reverse]. */
export const marketplaceRows = (mp) => {
    const th = mp?.th_percentiles || {};
    const ga = mp?.ga4_percentiles || {};
    return {
        marketplace: [
            { label: 'Impressions', block: th.impressions },
            { label: 'Details Views', block: th.details_views },
            { label: 'CTR', block: th.ctr, unit: '%', digits: 2 },
            { label: 'Email Leads', block: th.email_leads },
            { label: 'Watchlists', block: th.watchlists },
        ],
        website: [
            { label: 'Pageviews', block: ga.pageviews },
            { label: 'Unique Users', block: ga.unique_users },
            { label: 'Avg Session', block: ga.avg_session_sec, unit: 's' },
            { label: 'Bounce Rate', block: ga.bounce_rate, unit: '%', digits: 1, reverse: true },
        ],
    };
};

export const marketplaceTrends = (mp) => {
    const th = mp?.th_trend_7d_vs_prior || {};
    const ga = mp?.ga4_trend_7d_vs_prior || {};
    return [
        { label: 'Impressions', value: th.impressions_pct },
        { label: 'Leads', value: th.leads_pct },
        { label: 'Watchlists', value: th.watchlists_pct },
        { label: 'Pageviews', value: ga.pageviews_pct },
        { label: 'Avg Session', value: ga.session_pct },
    ];
};

/** The flag badges: Price friction, Reach dropping, Warm buyers now, … */
export function marketplaceBadges(mp) {
    const badges = [];
    if (!mp) return badges;
    if (mp.watchlist_without_lead?.flag) badges.push({ key: 'friction', label: 'Price friction' });
    if (mp.dead_listing_warning?.flag) badges.push({ key: 'reach', label: 'Reach dropping' });
    if (mp.warm_buyer_signal?.flag) badges.push({ key: 'warm', label: 'Warm buyers now' });
    if (mp.cohort_slowdown?.is_market_wide) badges.push({ key: 'slowdown', label: 'Market-wide slowdown' });
    if (mp.featured_roi?.verdict === 'not_paying_off') badges.push({ key: 'featured', label: 'Featured not paying' });
    return badges;
}

/** The insight strip's sentences, today's wording. */
export function marketplaceInsights(mp) {
    const out = [];
    if (!mp) return out;
    const wwl = mp.watchlist_without_lead;
    const dead = mp.dead_listing_warning;
    const warm = mp.warm_buyer_signal;
    const slowdown = mp.cohort_slowdown;
    const featured = mp.featured_roi;
    const projection = mp.price_cut_projection;
    if (wwl?.flag) out.push({ key: 'friction', text: `${fmtNum(wwl.watchlist_count)} buyers saved this, 0 emailed — price friction signal.` });
    if (dead?.flag) out.push({ key: 'reach', text: `${SOURCE_LABELS.marketplace} impressions down ${fmtPct(dead.impressions_14d_decline_pct)} over 14d — the listing may be losing visibility.` });
    if (warm?.flag) out.push({ key: 'warm', text: `Watchlist +${fmtNum(warm.watchlist_added_7d)} in last 7 days — warm buyer pool.` });
    if (slowdown?.is_market_wide) out.push({ key: 'slowdown', text: `Cohort-wide impressions down ${fmtPct(slowdown.cohort_impressions_trend_pct)} — category slowdown, not unit-specific.` });
    if (featured?.verdict === 'not_paying_off') out.push({ key: 'featured', text: `Featured CTR ${fmtNum(featured.cohort_ctr_featured, 2)}% vs non-featured ${fmtNum(featured.cohort_ctr_non_featured, 2)}% — Featured placement underperforming for this category.` });
    if (projection) out.push({ key: 'projection', text: `Projected price-drop impact: ${fmtNum(projection.current_watchlists)} buyers auto-notified by the ${SOURCE_LABELS.marketplaceLower}, est. ${fmtNum(projection.expected_leads_estimate)} leads at cohort conversion rate.` });
    return out;
}

// ── Sell-speed risk badge (P-UNIT-048) ───────────────────────────────────

const TIER_LEVEL = { Red: 'High', Yellow: 'Medium', Green: 'Low' };

// The badge shows the model's sell-speed risk tier — never the LLM's
// `urgency`. Briefs cached before the backend sent the tier fall back to the
// unit's own risk fields; an unscored unit shows no badge.
export function resolveRiskBadge(agent, unit) {
    const fromAgent = agent?.risk_tier != null;
    const tier = fromAgent ? agent.risk_tier : unit?.RiskTier;
    const level = (fromAgent && agent.risk_level) || TIER_LEVEL[tier];
    if (!level) return null;
    const rawPct = fromAgent ? agent.risk_pct : unit?.LadenhueterRisk;
    const pct = rawPct != null && Number.isFinite(Number(rawPct)) ? Math.round(Number(rawPct)) : null;
    return { tier, level, pct };
}

export const riskBadgeTitle = (badge) => (badge?.pct != null
    ? `Sell-speed risk: ${badge.pct}% risk of taking 180+ days to sell`
    : 'Sell-speed risk');

// ── Cached timestamp (P-UNIT-049) ────────────────────────────────────────

// An ISO date-time with no offset (Z or ±hh:mm) at the end.
const NAIVE_ISO = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

/**
 * The API's generated_at as an instant. It is UTC: newer answers say so
 * (+00:00); a naive value (cached before that) is read as UTC as well, not
 * as the browser's local time (QA L3).
 */
export const parseApiUtc = (value) => {
    const text = String(value || '').trim();
    return new Date(NAIVE_ISO.test(text) ? `${text}Z` : text);
};

/** "Updated Oct 7 · 06:12 AM" (browser time) from the real generated_at of a cached answer. */
export function cachedLabel(agent) {
    if (!agent?.cached || !agent.generated_at) return null;
    const d = parseApiUtc(agent.generated_at);
    if (Number.isNaN(d.getTime())) return null;
    return `Updated ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · ${d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
}

// ── Peers / listing comparison (P-UNIT-056 / 057) ────────────────────────

export const peerVerdictArrow = (v) => (v === 'Outperforming' ? '▲' : v === 'Underperforming' ? '▼' : '→');

export const mismatchText = (lc) => (lc?.price_mismatch
    ? `⚠ Mismatch: ${lc.price_mismatch_delta > 0 ? '+' : ''}$${lc.price_mismatch_delta?.toLocaleString('en-US')}`
    : '✓ Match');

// ── Create task from a recommendation (P-UNIT-054 / 126) ─────────────────

export const AI_DESCRIPTION_SEPARATOR = '--- AI-Generated Description ---';

/** The users the assignee picker offers (the auth-bypass user never). */
export const agentTaskUsers = (users) => (users || []).filter((u) => !String(u).includes('disable-auth'));

/** The form's prefill for a recommendation. */
export const taskPrefill = (rec, agent) => ({
    title: rec?.action || '',
    notes: rec?.rationale || '',
    description: agent?.suggested_description || '',
    type: rec?.activity_type || 'other',
    suggestedPrice: rec?.suggested_price || '',
    targetContact: rec?.target_contact || '',
});

/** The POST /api/activities body, exactly as today. */
export function buildTaskPayload(unit, form) {
    let details = form.notes || '';
    if (form.type === 'description' && form.description) {
        details = `${details}\n\n${AI_DESCRIPTION_SEPARATOR}\n${form.description}`;
    }
    return {
        stock_number: unit.StockNumber || String(unit.stock_number || ''),
        type: form.type,
        title: form.title,
        details: details.trim(),
        suggested_price: form.suggestedPrice ? Number(form.suggestedPrice) : null,
        current_price: unit.Best_Sell_Price || unit.price || null,
        assigned_to: form.assignee || null,
        due_date: form.dueDate ? new Date(form.dueDate).toISOString() : null,
    };
}
